/**
 * ForgotPasswordPage - Request password reset link
 * MODULE: core/auth/frontend/pages/ForgotPasswordPage.tsx
 * PURPOSE: Public forgot-password form with mobile-shell layout fork
 * DEPENDENCIES: flowbite-react, react-router-dom, @shared/lib/frontend/api/config, useMobileShellActive
 * PUBLIC_API: ForgotPasswordPage
 * SIDE_EFFECTS: POST /api/v1/auth/password/forgot
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-09 [2.2.1361] — min-h-dvh, safe-area, 44px submit/back, data-testid
 */
import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import { useMobileShellActive } from '@shared/lib/frontend/hooks/useMobileShellActive';
import { Card, TextInput, Label, Button, Alert, Spinner } from 'flowbite-react';

const API_BASE = `${getApiBaseUrl()}/api/v1/auth`;

type Status = 'idle' | 'loading' | 'success' | 'error';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const isMobileShell = useMobileShellActive();

  const submitBtnClass = `w-full${isMobileShell ? ' min-h-[44px]' : ''}`;
  const backLinkClass = `text-blue-600 hover:underline dark:text-blue-400${
    isMobileShell ? ' inline-flex min-h-[44px] items-center justify-center' : ''
  }`;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setStatus('loading');
    try {
      const res = await fetch(`${API_BASE}/password/forgot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Request failed');
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong');
    }
  };

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  return (
    <div
      className={`flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4 ${
        isMobileShell
          ? 'min-h-dvh py-6 pb-[max(1rem,env(safe-area-inset-bottom))]'
          : 'min-h-screen'
      }`}
      data-testid="forgot-password-page"
      data-mobile-shell={isMobileShell ? 'true' : undefined}
    >
      <Card className={`max-w-md w-full mx-auto ${isMobileShell ? 'p-4' : 'p-6'}`}>
        <div className={`text-center ${isMobileShell ? 'mb-4' : 'mb-6'}`}>
          <h1 className={`font-bold text-gray-900 dark:text-white ${isMobileShell ? 'text-xl' : 'text-2xl'}`}>
            Brazn
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Enterprise Agentic Platform</p>
        </div>
        <h2
          className={`font-semibold text-gray-900 dark:text-white mb-1 ${
            isMobileShell ? 'text-lg' : 'text-xl'
          }`}
        >
          Forgot your password?
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Enter your email and we&apos;ll send you a reset link.
        </p>

        {status === 'success' ? (
          <Alert color="success" className="mb-4">
            Check your email for a reset link. If you don&apos;t see it, check your spam folder.
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert color="failure" onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <TextInput
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={status === 'loading'}
                className="mt-1"
              />
            </div>
            <Button type="submit" className={submitBtnClass} disabled={status === 'loading' || !isValidEmail}>
              {status === 'loading' ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>
        )}

        <p className={`mt-6 text-center text-sm text-gray-600 ${isMobileShell ? 'dark:text-gray-400' : ''}`}>
          <Link to="/login" className={backLinkClass}>
            Back to login
          </Link>
        </p>
      </Card>
    </div>
  );
}
